/**
 * Appoint Director — 수동 입력용 구조화 프로필 (주소 / ID / 휴대폰)
 * 문서 변수(director_address)는 조합된 표시 문자열로 sync.
 */

export type AddressTypeCd = 'B07001' | 'B07002' | '';

export interface DirectorStructuredAddress {
  addressTypeCd: AddressTypeCd;
  postalCode: string;
  blockNo: string;
  streetName: string;
  buildingName: string;
  addrLevel: string;
  addrUnit: string;
  foreignLine1: string;
  foreignLine2: string;
}

export interface DirectorStructuredProfile {
  idTypeCd: string;
  mobileCountryCd: string;
  mobileAreaCode: string;
  primary: DirectorStructuredAddress;
  alternate: DirectorStructuredAddress;
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export function createEmptyDirectorStructuredAddress(
  typeCd: AddressTypeCd = 'B07001',
): DirectorStructuredAddress {
  return {
    addressTypeCd: typeCd,
    postalCode: '',
    blockNo: '',
    streetName: '',
    buildingName: '',
    addrLevel: '',
    addrUnit: '',
    foreignLine1: '',
    foreignLine2: '',
  };
}

export function createEmptyDirectorStructuredProfile(): DirectorStructuredProfile {
  return {
    idTypeCd: '',
    mobileCountryCd: '',
    mobileAreaCode: '',
    primary: createEmptyDirectorStructuredAddress('B07001'),
    alternate: createEmptyDirectorStructuredAddress(''),
  };
}

/** Local / Foreign → 문서·Form 45용 한 줄 주소 */
export function formatDirectorStructuredAddress(addr: DirectorStructuredAddress): string {
  const type = trim(addr.addressTypeCd) || 'B07001';
  if (type === 'B07002') {
    const f1 = trim(addr.foreignLine1).replace(/,\s*$/, '');
    const f2 = trim(addr.foreignLine2);
    return [f1, f2].filter(Boolean).join(', ');
  }

  const blockNo = trim(addr.blockNo);
  const streetName = trim(addr.streetName);
  const line1 = [blockNo, streetName].filter(Boolean).join(' ');
  const levelUnit =
    trim(addr.addrLevel) || trim(addr.addrUnit)
      ? `#${trim(addr.addrLevel)}${trim(addr.addrUnit) ? `-${trim(addr.addrUnit)}` : ''}`.replace(/^#-/, '#')
      : '';
  const postal = trim(addr.postalCode);

  return [
    line1,
    levelUnit === '#' ? '' : levelUnit,
    trim(addr.buildingName),
    postal ? `SINGAPORE ${postal}` : '',
  ]
    .filter(Boolean)
    .join(', ');
}

export function formatDirectorMobileDisplay(areaCode: string, mobileNo: string): string {
  const ac = trim(areaCode);
  const no = trim(mobileNo);
  if (ac && no) return `+${ac} ${no}`;
  return no;
}

/** contactData / 챗봇 스냅샷 → 구조화 주소 */
export function directorStructuredAddressFromContact(
  cd: Record<string, unknown> | null | undefined,
  kind: 'primary' | 'alternate',
): DirectorStructuredAddress {
  const c = cd && typeof cd === 'object' ? cd : {};
  const isAlt = kind === 'alternate';
  const typeCd = trim(
    isAlt
      ? c.ALTERNATE_ADDRESS_CD ?? c.alternateAddressCd
      : c.ADDRESS_TYPE_CD ?? c.addressTypeCd,
  ) as AddressTypeCd;

  if (typeCd === 'B07002') {
    return {
      addressTypeCd: 'B07002',
      postalCode: '',
      blockNo: '',
      streetName: '',
      buildingName: '',
      addrLevel: '',
      addrUnit: '',
      foreignLine1: trim(
        isAlt ? c.ALT_FOREIGN_ADDRESS_LINE_1 ?? c.altForeignAddressLine1 : c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1,
      ),
      foreignLine2: trim(
        isAlt ? c.ALT_FOREIGN_ADDRESS_LINE_2 ?? c.altForeignAddressLine2 : c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2,
      ),
    };
  }

  if (typeCd === 'B07001') {
    return {
      addressTypeCd: 'B07001',
      postalCode: trim(isAlt ? c.ALT_POSTAL_CODE ?? c.altPostalCode : c.POSTAL_CODE ?? c.postalCode),
      blockNo: trim(isAlt ? c.ALT_BLOCK_NO ?? c.altBlockNo : c.BLOCK_NO ?? c.blockNo),
      streetName: trim(isAlt ? c.ALT_STREET_NAME ?? c.altStreetName : c.STREET_NAME ?? c.streetName),
      buildingName: trim(isAlt ? c.ALT_BUILDING_NAME ?? c.altBuildingName : c.BUILDING_NAME ?? c.buildingName),
      addrLevel: trim(isAlt ? c.ALT_ADDR_LEVEL ?? c.altAddrLevel : c.ADDR_LEVEL ?? c.addrLevel),
      addrUnit: trim(isAlt ? c.ALT_ADDR_UNIT ?? c.altAddrUnit : c.ADDR_UNIT ?? c.addrUnit),
      foreignLine1: '',
      foreignLine2: '',
    };
  }

  return createEmptyDirectorStructuredAddress('');
}

export function applyContactDataToDirectorStructuredProfile(
  cd: Record<string, unknown> | null | undefined,
): DirectorStructuredProfile {
  const c = cd && typeof cd === 'object' ? cd : {};
  return {
    idTypeCd: trim(c.ID_TYPE_CD ?? c.idTypeCd),
    mobileCountryCd: trim(c.COUNTRY_CD ?? c.countryCd),
    mobileAreaCode: trim(c.AREA_CODE ?? c.areaCode),
    primary: directorStructuredAddressFromContact(c, 'primary'),
    alternate: directorStructuredAddressFromContact(c, 'alternate'),
  };
}

/** flat model 필드 키 — useFormPayload / gatherState 와 동일 */
export function pickDirectorProfileFromFlat(src: Record<string, unknown>): DirectorStructuredProfile {
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = src[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  return {
    idTypeCd: pick('directorIdTypeCd', 'secretaryIdTypeCd', 'idTypeCd', 'ID_TYPE_CD'),
    mobileCountryCd: pick('directorMobileCountryCd', 'secretaryMobileCountryCd', 'COUNTRY_CD', 'countryCd'),
    mobileAreaCode: pick('directorMobileAreaCode', 'secretaryMobileAreaCode', 'AREA_CODE', 'areaCode'),
    primary: {
      addressTypeCd: pick('directorAddressTypeCd', 'secretaryAddressTypeCd', 'ADDRESS_TYPE_CD', 'addressTypeCd') as AddressTypeCd,
      postalCode: pick('directorPostalCode', 'secretaryPostalCode', 'POSTAL_CODE', 'postalCode'),
      blockNo: pick('directorBlockNo', 'secretaryBlockNo', 'BLOCK_NO', 'blockNo'),
      streetName: pick('directorStreetName', 'secretaryStreetName', 'STREET_NAME', 'streetName'),
      buildingName: pick('directorBuildingName', 'secretaryBuildingName', 'BUILDING_NAME', 'buildingName'),
      addrLevel: pick('directorAddrLevel', 'secretaryAddrLevel', 'ADDR_LEVEL', 'addrLevel'),
      addrUnit: pick('directorAddrUnit', 'secretaryAddrUnit', 'ADDR_UNIT', 'addrUnit'),
      foreignLine1: pick('directorForeignLine1', 'secretaryForeignLine1', 'FOREIGN_ADDRESS_LINE_1', 'foreignAddressLine1'),
      foreignLine2: pick('directorForeignLine2', 'secretaryForeignLine2', 'FOREIGN_ADDRESS_LINE_2', 'foreignAddressLine2'),
    },
    alternate: {
      addressTypeCd: pick('directorAltAddressTypeCd', 'secretaryAltAddressTypeCd', 'ALTERNATE_ADDRESS_CD', 'alternateAddressCd') as AddressTypeCd,
      postalCode: pick('directorAltPostalCode', 'secretaryAltPostalCode', 'ALT_POSTAL_CODE', 'altPostalCode'),
      blockNo: pick('directorAltBlockNo', 'secretaryAltBlockNo', 'ALT_BLOCK_NO', 'altBlockNo'),
      streetName: pick('directorAltStreetName', 'secretaryAltStreetName', 'ALT_STREET_NAME', 'altStreetName'),
      buildingName: pick('directorAltBuildingName', 'secretaryAltBuildingName', 'ALT_BUILDING_NAME', 'altBuildingName'),
      addrLevel: pick('directorAltAddrLevel', 'secretaryAltAddrLevel', 'ALT_ADDR_LEVEL', 'altAddrLevel'),
      addrUnit: pick('directorAltAddrUnit', 'secretaryAltAddrUnit', 'ALT_ADDR_UNIT', 'altAddrUnit'),
      foreignLine1: pick('directorAltForeignLine1', 'secretaryAltForeignLine1', 'ALT_FOREIGN_ADDRESS_LINE_1', 'altForeignAddressLine1'),
      foreignLine2: pick('directorAltForeignLine2', 'secretaryAltForeignLine2', 'ALT_FOREIGN_ADDRESS_LINE_2', 'altForeignAddressLine2'),
    },
  };
}

export function directorProfileToFlatFields(profile: DirectorStructuredProfile): Record<string, string> {
  const p = profile.primary;
  const a = profile.alternate;
  return {
    directorIdTypeCd: profile.idTypeCd,
    directorMobileCountryCd: profile.mobileCountryCd,
    directorMobileAreaCode: profile.mobileAreaCode,
    directorAddressTypeCd: p.addressTypeCd,
    directorPostalCode: p.postalCode,
    directorBlockNo: p.blockNo,
    directorStreetName: p.streetName,
    directorBuildingName: p.buildingName,
    directorAddrLevel: p.addrLevel,
    directorAddrUnit: p.addrUnit,
    directorForeignLine1: p.foreignLine1,
    directorForeignLine2: p.foreignLine2,
    directorAltAddressTypeCd: a.addressTypeCd,
    directorAltPostalCode: a.postalCode,
    directorAltBlockNo: a.blockNo,
    directorAltStreetName: a.streetName,
    directorAltBuildingName: a.buildingName,
    directorAltAddrLevel: a.addrLevel,
    directorAltAddrUnit: a.addrUnit,
    directorAltForeignLine1: a.foreignLine1,
    directorAltForeignLine2: a.foreignLine2,
  };
}

export function directorProfileToContactData(
  profile: DirectorStructuredProfile,
  base: Record<string, unknown>,
): Record<string, unknown> {
  const p = profile.primary;
  const a = profile.alternate;
  const altHasData = [
    a.addressTypeCd,
    a.postalCode,
    a.blockNo,
    a.streetName,
    a.foreignLine1,
  ].some((v) => trim(v));

  return {
    ...base,
    ID_TYPE_CD: profile.idTypeCd,
    idTypeCd: profile.idTypeCd,
    COUNTRY_CD: profile.mobileCountryCd,
    countryCd: profile.mobileCountryCd,
    AREA_CODE: profile.mobileAreaCode,
    areaCode: profile.mobileAreaCode,
    ADDRESS_TYPE_CD: p.addressTypeCd || 'B07001',
    addressTypeCd: p.addressTypeCd || 'B07001',
    POSTAL_CODE: p.addressTypeCd === 'B07001' ? p.postalCode : '',
    BLOCK_NO: p.addressTypeCd === 'B07001' ? p.blockNo : '',
    STREET_NAME: p.addressTypeCd === 'B07001' ? p.streetName : '',
    BUILDING_NAME: p.addressTypeCd === 'B07001' ? p.buildingName : '',
    ADDR_LEVEL: p.addressTypeCd === 'B07001' ? p.addrLevel : '',
    ADDR_UNIT: p.addressTypeCd === 'B07001' ? p.addrUnit : '',
    FOREIGN_ADDRESS_LINE_1: p.addressTypeCd === 'B07002' ? p.foreignLine1 : '',
    FOREIGN_ADDRESS_LINE_2: p.addressTypeCd === 'B07002' ? p.foreignLine2 : '',
    foreignAddressLine1: p.addressTypeCd === 'B07002' ? p.foreignLine1 : '',
    foreignAddressLine2: p.addressTypeCd === 'B07002' ? p.foreignLine2 : '',
    ALTERNATE_ADDRESS_CD: altHasData ? (a.addressTypeCd || 'B07001') : '',
    alternateAddressCd: altHasData ? (a.addressTypeCd || 'B07001') : '',
    ALT_POSTAL_CODE: a.addressTypeCd === 'B07001' ? a.postalCode : '',
    ALT_BLOCK_NO: a.addressTypeCd === 'B07001' ? a.blockNo : '',
    ALT_STREET_NAME: a.addressTypeCd === 'B07001' ? a.streetName : '',
    ALT_BUILDING_NAME: a.addressTypeCd === 'B07001' ? a.buildingName : '',
    ALT_ADDR_LEVEL: a.addressTypeCd === 'B07001' ? a.addrLevel : '',
    ALT_ADDR_UNIT: a.addressTypeCd === 'B07001' ? a.addrUnit : '',
    ALT_FOREIGN_ADDRESS_LINE_1: a.addressTypeCd === 'B07002' ? a.foreignLine1 : '',
    ALT_FOREIGN_ADDRESS_LINE_2: a.addressTypeCd === 'B07002' ? a.foreignLine2 : '',
  };
}

export function syncDirectorDisplayFromStructured(model: {
  directorPrimaryAddress?: DirectorStructuredAddress;
  directorAlternateAddressParts?: DirectorStructuredAddress;
  directorAddress: string;
  directorAlternateAddress: string;
  directorMobileAreaCode?: string;
  directorContactNo: string;
}): void {
  const primary = model.directorPrimaryAddress ?? createEmptyDirectorStructuredAddress('B07001');
  const alternate = model.directorAlternateAddressParts ?? createEmptyDirectorStructuredAddress('');
  model.directorAddress = formatDirectorStructuredAddress(primary);
  const altText = formatDirectorStructuredAddress(alternate);
  model.directorAlternateAddress = altText;
}

export function isDirectorStructuredAddressComplete(addr: DirectorStructuredAddress): boolean {
  const type = trim(addr.addressTypeCd) || 'B07001';
  if (type === 'B07002') return !!trim(addr.foreignLine1);
  return !!trim(addr.postalCode) && !!(trim(addr.streetName) || trim(addr.blockNo));
}
